import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Article,
  Contractor,
  Contract,
  Employee,
  PlanfactOperation,
  Source,
  Task,
  WorkType,
  BankAccount,
  getArticles,
  getContract,
  getContractor,
  getContractFormAssignees,
  getContractors,
  getContracts,
  getPlanfactOperations,
  getSources,
  getTasks,
  getWorkTypes,
  getBankAccounts,
  updateContract,
  updateTask,
  getContractStages,
  replaceContractStages,
  type ContractNote,
  type ContractStage,
} from '../services/api';
import {
  displayContractSumDraft,
  formatContractSumDisplay,
  formatDate,
  formatDateShort,
  normalizeContractSumDraft,
  parseContractSumInput,
} from '../utils/table';
import { formatFinanceCell, formatSignedBalance } from '../utils/taskFinance';
import {
  maxFamilyDate,
  resolveContractFamily,
  sumFamilyAmount,
} from '../utils/contractFamily';
import { formatDeadlineRelativeTo } from '../utils/deadline';
import { contractStatusBadgeVariant } from '../utils/contractStatus';
import { sumContractFamilyReceiptsFromOperations, sumFamilyTaskActualExpensesFromOperations, sumFamilyTaskReceiptsFromOperations } from '../utils/contractReceipts';
import {
  ALL_TASK_EXPENSE_EXTRAS,
  TASK_BALANCE_EXPENSES_ROW_ID,
  buildContractFamilyBalanceTable,
  buildTaskExpensesTable,
  collectOperationsForContractFamilyBalanceCell,
  excludeHiddenExpenseCategoryOperations,
  filterContractFamilyOperations,
  type TaskBalanceColumnKey,
} from '../utils/taskBalance';
import { expenseAmountMagnitude, isOperationReceipt } from '../utils/operationBudgetArticle';
import { useAuth } from '../context/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import BudgetProgressBar from '../components/BudgetProgressBar';
import BalanceBarChart from '../components/BalanceBarChart';
import TaskDeadlinesGantt, { type TaskDeadlineGanttRow } from '../components/TaskDeadlinesGantt';
import { buildContractStageDeadlinesData, resolveFamilyExpenseClosingSummary, resolveFamilyStageClosingSummary, resolveFamilyStageReferenceDate, type DeadlineStatusVariant } from '../utils/contractDeadlines';
import { buildContractReceiptsTabData } from '../utils/contractReceiptsTab';
import PaymentDonut from '../components/PaymentDonut';
import ContractPaymentNotesChat from '../components/ContractPaymentNotesChat';
import ContractPaymentCalendarTable from '../components/ContractPaymentCalendarTable';
import {
  ContractStagesEditor,
  contractDatesFromStages,
  stagesFromServer,
  stagesToWrite,
  validateStageDrafts,
  groupStageDrafts,
  type StageDraft,
} from '../components/ContractStagesTable';
import MarginCalcTable from '../components/MarginCalcTable';
import TaskBalanceTable, { type TaskBalanceCellClick } from '../components/TaskBalanceTable';
import TaskOperationsTable from '../components/TaskOperationsTable';
import {
  buildMarginCalcHeaderCells,
  buildMarginCalcTableData,
  buildMarginFinanceRows,
  buildReadOnlyMarginValueGetter,
  resolveContractTasksAmountMismatch,
  sortMarginCalcColumns,
  sumTasksPlannedExpenses,
} from '../utils/marginCalc';
import { ContractorPicker } from '../components/ContractorPicker';
import { SourcePicker } from '../components/SourcePicker';
import '../styles/DataPage.css';
import '../styles/ContractorDetailPage.css';
import './TaskDetailPage.css';
import './ContractDetailPage.css';

type ContractCardTabId = 'general' | 'budget' | 'receipts' | 'balance' | 'deadlines' | 'workSchedule';

type ContractOpsViewFilter =
  | { kind: 'all' }
  | {
      kind: 'balanceCell';
      rowId: string;
      rowLabel: string;
      columnKey: TaskBalanceColumnKey;
      columnLabel: string;
    };

const CARD_EDIT_TAB_IDS: ContractCardTabId[] = ['general', 'deadlines', 'receipts', 'workSchedule'];

interface ContractDeadlineDraft {
  start_date: string;
  end_date: string;
}

function sortEmployeesByName(list: Employee[]): Employee[] {
  return [...list].sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru'));
}

function employeesForRoleSelect(
  roleEmployees: Employee[],
  selectedId: number | null,
  allEmployees: Employee[],
): Employee[] {
  const byId = new Map(roleEmployees.map((e) => [e.id, e]));
  if (selectedId != null && !byId.has(selectedId)) {
    const selected = allEmployees.find((e) => e.id === selectedId);
    if (selected) {
      byId.set(selected.id, selected);
    }
  }
  return sortEmployeesByName([...byId.values()]);
}

function taskDetailPath(task: Pick<Task, 'id' | 'contracts_task_id'>): string {
  return `/tasks/${task.id}?contracts_task_id=${task.contracts_task_id}`;
}

function deadlineStatusBadgeClass(variant: DeadlineStatusVariant): string {
  return `contract-detail__deadline-badge contract-detail__deadline-badge--${variant}`;
}

function deadlineDeltaCellClass(): string {
  return 'task-detail__deadlines-delta task-detail__deadlines-date';
}

function parseStageDay(value?: string | null): number | null {
  if (!value?.trim()) return null;
  const ms = new Date(`${value.trim()}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function sameIdList(left: number[], right: number[]): boolean {
  if (left.length !== right.length) return false;
  const sortedLeft = [...left].sort((a, b) => a - b);
  const sortedRight = [...right].sort((a, b) => a - b);
  return sortedLeft.every((value, index) => value === sortedRight[index]);
}

function StatusCheckIcon({ done, label }: { done: boolean; label: string }): React.ReactElement {
  return (
    <span
      className={`contract-detail__status-check${done ? ' contract-detail__status-check--done' : ''}`}
      aria-label={done ? `${label}: да` : `${label}: нет`}
      title={done ? 'Да' : 'Нет'}
    >
      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
        {done ? (
          <>
            <circle cx="8" cy="8" r="7" fill="#1f9d55" stroke="#167a40" strokeWidth="1" />
            <path
              d="M4.6 8.2 7 10.6l4.4-5.2"
              fill="none"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        ) : (
          <circle cx="8" cy="8" r="6.5" fill="none" stroke="#8a94a6" strokeWidth="1.5" />
        )}
      </svg>
    </span>
  );
}

function formatFactExpensesSum(value: number): string {
  return formatContractSumDisplay(expenseAmountMagnitude(value));
}

const ContractDetailPage: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can, hasDepartment, hasRole, user, isAdmin, expensePlanfactArticles } = useAuth();

  const canOpenContractCard =
    hasDepartment('ALL_COMP') || hasRole('ОП_Менеджер', 'ОУП_Рук. проекта');
  const canContracts = can('CONTRACTS', 'VIEW');
  const canEditContract = can('CONTRACTS', 'EDIT');
  const canContractors = can('CONTRACTORS', 'VIEW');
  const canTasks = can('TASKS', 'VIEW');
  const canTasksEdit = can('TASKS', 'EDIT');
  const canSum = can('CONTRACT_SUM', 'VIEW');
  const canEditSum = can('CONTRACT_SUM', 'EDIT');
  const canEditSumStages = can('CONTRACT_SUM_STAGES', 'EDIT');
  const canMargin = can('CONTRACT_MARGIN', 'VIEW');
  const canEditPrimarySource = can('CONTRACT_SOURCE_FIRST', 'EDIT');
  const canIncome = can('INCOME', 'VIEW');
  const canOutcome = can('OUTCOME', 'VIEW');
  const canOutcomeCash = isAdmin || can('OUTCOME_CASH', 'VIEW');
  const canOutcomeServ = isAdmin || can('OUTCOME_SERV', 'VIEW');
  const canOutcomeTax = isAdmin || can('OUTCOME_TAX', 'VIEW');
  const canOutcomePartner = isAdmin || can('OUTCOME_PARTNER', 'VIEW');
  const canOutcomePlan = can('OUTCOME_PLAN', 'VIEW');
  const canIncomePlanningView = can('INCOME_PLANNING', 'VIEW');
  const canIncomePlanningEdit = can('INCOME_PLANNING', 'EDIT');
  const canReceiptsTab = canSum && canIncome;
  const canCashFlow = canSum && canIncome && canOutcome && canMargin;
  const canBalanceTab = canCashFlow;
  const canBudgetTab = canMargin && canSum;
  const canLoadOperations = canReceiptsTab || canBalanceTab || canOutcome;

  const contractPk = idParam ? parseInt(idParam, 10) : NaN;

  const [contract, setContract] = useState<Contract | null>(null);
  const [allContracts, setAllContracts] = useState<Contract[]>([]);
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [planfactOperations, setPlanfactOperations] = useState<PlanfactOperation[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [opsViewFilter, setOpsViewFilter] = useState<ContractOpsViewFilter>({ kind: 'all' });
  const [contractor, setContractor] = useState<Contractor | null>(null);
  const [allContractors, setAllContractors] = useState<Contractor[]>([]);
  const [managerEmployees, setManagerEmployees] = useState<Employee[]>([]);
  const [projectLeadEmployees, setProjectLeadEmployees] = useState<Employee[]>([]);
  const [contractSourcesLevel2, setContractSourcesLevel2] = useState<Source[]>([]);
  const [contractSourcesLevel1, setContractSourcesLevel1] = useState<Source[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useDocumentTitle(contract?.contract_number || 'Договор');

  const [headerNumber, setHeaderNumber] = useState('');
  const [headerSaving, setHeaderSaving] = useState(false);
  const [headerError, setHeaderError] = useState('');

  const [expandedContractIds, setExpandedContractIds] = useState<Set<number>>(() => new Set());
  const [expandedReceiptsIds, setExpandedReceiptsIds] = useState<Set<number>>(() => new Set());
  const [mountedReceiptsIds, setMountedReceiptsIds] = useState<Set<number>>(() => new Set());
  const [calendarEpoch, setCalendarEpoch] = useState(0);
  const calendarPersistersRef = useRef(new Map<number, () => Promise<void>>());
  const [deadlinesTasksExpanded, setDeadlinesTasksExpanded] = useState(false);
  const [activeCardTab, setActiveCardTab] = useState<ContractCardTabId>('general');
  const [subjectDrafts, setSubjectDrafts] = useState<Record<number, string>>({});
  const [hasOriginalDrafts, setHasOriginalDrafts] = useState<Record<number, boolean>>({});
  const [contractorDraftId, setContractorDraftId] = useState<number | null>(null);
  const [managerDraftId, setManagerDraftId] = useState<number | null>(null);
  const [projectLeadDraftId, setProjectLeadDraftId] = useState<number | null>(null);
  const [sourceDraftId, setSourceDraftId] = useState<number | null>(null);
  const [primarySourceDraftId, setPrimarySourceDraftId] = useState<number | null>(null);
  const [addressDraft, setAddressDraft] = useState('');
  const [cadastralDraft, setCadastralDraft] = useState('');
  const [deadlineDrafts, setDeadlineDrafts] = useState<Record<number, ContractDeadlineDraft>>({});
  const [bankAccountDrafts, setBankAccountDrafts] = useState<Record<number, number | null>>({});
  const [amountDrafts, setAmountDrafts] = useState<Record<number, string>>({});
  const [cardEditing, setCardEditing] = useState(false);
  const [cardSaving, setCardSaving] = useState(false);
  const [cardError, setCardError] = useState('');
  const [paymentNotesOpen, setPaymentNotesOpen] = useState(false);
  const [workStages, setWorkStages] = useState<ContractStage[]>([]);
  const [stageDrafts, setStageDrafts] = useState<StageDraft[]>([]);
  const [taskStageDrafts, setTaskStageDrafts] = useState<Record<number, number[]>>({});

  const employeeById = useMemo(() => {
    const map = new Map<number, Employee>();
    for (const e of employees) {
      map.set(e.id, e);
    }
    return map;
  }, [employees]);

  const getEmployeeName = useCallback(
    (id?: number | null): string => {
      if (id == null) return '—';
      return employeeById.get(id)?.full_name || '—';
    },
    [employeeById],
  );

  const workTypeLabel = useCallback(
    (id: number | null | undefined): string => {
      if (id == null) return '—';
      const wt = workTypes.find((item) => item.id === id);
      return wt?.name ?? '—';
    },
    [workTypes],
  );

  const workTypeShortLabel = useCallback(
    (id: number | null | undefined): string => {
      if (id == null) return '—';
      const wt = workTypes.find((item) => item.id === id);
      return wt?.short_name || wt?.name || '—';
    },
    [workTypes],
  );

  const load = useCallback(async (): Promise<void> => {
    if (!canOpenContractCard || !canContracts || Number.isNaN(contractPk)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const row = await getContract(contractPk);
      setContract(row);
      setHeaderNumber(row.contract_number);
      const [contracts, tasks, assignees, operations, articleRows, types, sourcesLevel2, sourcesLevel1, accounts] = await Promise.all([
        getContracts(),
        canTasks || canBudgetTab || canOutcome
          ? getTasks().catch(() => [] as Task[])
          : Promise.resolve([] as Task[]),
        getContractFormAssignees(),
        canLoadOperations
          ? getPlanfactOperations().catch(() => [] as PlanfactOperation[])
          : Promise.resolve([] as PlanfactOperation[]),
        canLoadOperations
          ? getArticles().catch(() => [] as Article[])
          : Promise.resolve([] as Article[]),
        canTasks || canBudgetTab ? getWorkTypes().catch(() => [] as WorkType[]) : Promise.resolve([] as WorkType[]),
        getSources(2).catch(() => [] as Source[]),
        getSources(1).catch(() => [] as Source[]),
        getBankAccounts().catch(() => [] as BankAccount[]),
      ]);
      setAllContracts(contracts);
      const family = resolveContractFamily(row, contracts);
      const stageLists = await Promise.all(
        family.familyContracts.map((ct) => getContractStages(ct.id).catch(() => [] as ContractStage[])),
      );
      const stageRows = stageLists.flat();
      setWorkStages(stageRows);
      setStageDrafts(stagesFromServer(stageRows));
      setAllTasks(tasks);
      setWorkTypes(types);
      setPlanfactOperations(operations);
      setArticles(articleRows);
      setContractSourcesLevel2(sourcesLevel2);
      setContractSourcesLevel1(sourcesLevel1);
      setBankAccounts(accounts);
      setManagerEmployees(assignees.managers);
      setProjectLeadEmployees(assignees.project_leads);
      const emps = [...assignees.managers, ...assignees.project_leads];
      const byId = new Map<number, Employee>();
      for (const e of emps) {
        byId.set(e.id, e);
      }
      setEmployees([...byId.values()]);

      if (canContractors) {
        try {
          const [contractorRow, contractors] = await Promise.all([
            getContractor(row.contractor_id),
            getContractors(),
          ]);
          setContractor(contractorRow);
          setAllContractors(contractors);
        } catch {
          setContractor(null);
          setAllContractors([]);
        }
      } else {
        setContractor(null);
        setAllContractors([]);
      }
    } catch (e) {
      setError('Не удалось загрузить договор');
      setContract(null);
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [canOpenContractCard, canContracts, canTasks, canContractors, canReceiptsTab, canBalanceTab, canBudgetTab, canOutcome, canLoadOperations, contractPk]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setActiveCardTab('general');
    setCardEditing(false);
    setCardError('');
    setOpsViewFilter({ kind: 'all' });
    setPaymentNotesOpen(false);
    setExpandedReceiptsIds(new Set());
    setMountedReceiptsIds(new Set());
    setCalendarEpoch(0);
  }, [contractPk]);

  useEffect(() => {
    setDeadlinesTasksExpanded(false);
  }, [contractPk]);

  useEffect(() => {
    if (!contract) return;
    setHeaderNumber(contract.contract_number);
  }, [contract]);

  const family = useMemo(() => {
    if (!contract) return null;
    return resolveContractFamily(contract, allContracts);
  }, [contract, allContracts]);

  const familyContracts = useMemo(() => family?.familyContracts ?? [], [family]);
  const mainContract = useMemo(() => family?.mainContract ?? contract, [family, contract]);

  const syncCardDrafts = useCallback((): void => {
    const subjectByContract: Record<number, string> = {};
    const hasOriginalByContract: Record<number, boolean> = {};
    const deadlinesByContract: Record<number, ContractDeadlineDraft> = {};
    const bankAccountsByContract: Record<number, number | null> = {};
    const amountsByContract: Record<number, string> = {};
    for (const ct of familyContracts) {
      subjectByContract[ct.id] = ct.contract_subject ?? '';
      hasOriginalByContract[ct.id] = Boolean(ct.has_original);
      deadlinesByContract[ct.id] = {
        start_date: ct.start_date?.trim() ?? '',
        end_date: ct.end_date?.trim() ?? '',
      };
      bankAccountsByContract[ct.id] = ct.bank_account_id ?? null;
      amountsByContract[ct.id] = normalizeContractSumDraft(String(ct.total_amount ?? ''));
    }
    setSubjectDrafts(subjectByContract);
    setHasOriginalDrafts(hasOriginalByContract);
    setDeadlineDrafts(deadlinesByContract);
    setBankAccountDrafts(bankAccountsByContract);
    setAmountDrafts(amountsByContract);
    setAddressDraft(mainContract?.address ?? '');
    setCadastralDraft(mainContract?.cadastral_number ?? '');
    if (contract) {
      setContractorDraftId(contract.contractor_id);
      setManagerDraftId(contract.manager_id ?? null);
      setProjectLeadDraftId(contract.project_lead_id ?? null);
      setSourceDraftId(contract.source_id ?? null);
      setPrimarySourceDraftId(contract.primary_source_id ?? null);
    }
    setStageDrafts(stagesFromServer(workStages));
    if (contract) {
      const nextBindings: Record<number, number[]> = {};
      for (const task of allTasks) {
        if (task.contract_id !== contract.id) continue;
        nextBindings[task.contracts_task_id] = [...(task.contract_stage_ids ?? [])];
      }
      setTaskStageDrafts(nextBindings);
    } else {
      setTaskStageDrafts({});
    }
  }, [familyContracts, mainContract, contract, workStages, allTasks]);

  useEffect(() => {
    if (cardEditing) return;
    syncCardDrafts();
  }, [cardEditing, syncCardDrafts]);

  useEffect(() => {
    if (familyContracts.length > 0) {
      setExpandedContractIds(new Set(familyContracts.map((ct) => ct.id)));
    }
  }, [familyContracts]);

  useEffect(() => {
    if (contract?.id == null) return;
    setExpandedReceiptsIds((prev) => {
      if (prev.has(contract.id)) return prev;
      const next = new Set(prev);
      next.add(contract.id);
      return next;
    });
    setMountedReceiptsIds((prev) => {
      if (prev.has(contract.id)) return prev;
      const next = new Set(prev);
      next.add(contract.id);
      return next;
    });
  }, [contract?.id]);

  const familyTasks = useMemo(() => {
    if (!family) return [];
    return allTasks
      .filter((t) => family.familyIds.has(t.contract_id))
      .sort((a, b) => a.task_id.localeCompare(b.task_id, 'ru'));
  }, [allTasks, family]);

  const contractTasks = useMemo(() => {
    if (!contract) return [];
    return allTasks
      .filter((task) => task.contract_id === contract.id)
      .sort((a, b) => {
        const byId = a.task_id.localeCompare(b.task_id, 'ru', { numeric: true });
        if (byId !== 0) return byId;
        return workTypeLabel(a.work_type_id).localeCompare(workTypeLabel(b.work_type_id), 'ru');
      });
  }, [allTasks, contract, workTypeLabel]);

  const stageTaskOptions = useMemo(() => {
    const groups: Array<{ taskId: string; tasks: Task[] }> = [];
    const indexByTaskId = new Map<string, number>();
    for (const task of contractTasks) {
      const existing = indexByTaskId.get(task.task_id);
      if (existing == null) {
        indexByTaskId.set(task.task_id, groups.length);
        groups.push({ taskId: task.task_id, tasks: [task] });
      } else {
        groups[existing].tasks.push(task);
      }
    }
    return groups.map(({ tasks }) => {
      const shortNames: string[] = [];
      for (const task of tasks) {
        const shortName = workTypeShortLabel(task.work_type_id);
        if (!shortNames.includes(shortName)) shortNames.push(shortName);
      }
      return {
        id: String(tasks[0].contracts_task_id),
        memberIds: tasks.map((task) => String(task.contracts_task_id)),
        label: shortNames.join(', ') || '—',
      };
    });
  }, [contractTasks, workTypeShortLabel]);

  const boundTaskIdsByStageId = useMemo(() => {
    const result: Record<number, string[]> = {};
    for (const task of contractTasks) {
      const stageIds = cardEditing
        ? (taskStageDrafts[task.contracts_task_id] ?? [])
        : (task.contract_stage_ids ?? []);
      for (const stageId of stageIds) {
        (result[stageId] ??= []).push(String(task.contracts_task_id));
      }
    }
    return result;
  }, [cardEditing, contractTasks, taskStageDrafts]);

  const stageContractOptions = useMemo(
    () =>
      familyContracts.map((ct) => ({
        id: ct.id,
        label: ct.contract_number,
        hierarchyLevel: ct.hierarchy_level ?? 1,
      })),
    [familyContracts],
  );

  const workScheduleGantt = useMemo(() => {
    if (cardEditing) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();
    const groups = groupStageDrafts(stageDrafts);
    const rows: TaskDeadlineGanttRow[] = groups.map((group) => {
      const endMs = parseStageDay(group.parent.planned_date_end);
      const extraBars = group.extensions.map((extension) => {
        const extraEnd = parseStageDay(extension.planned_date_end);
        return {
          startMs: parseStageDay(extension.planned_date_start),
          endMs: extraEnd,
          barOverdue: extraEnd != null && extraEnd < todayMs,
        };
      });
      return {
        key: group.parent.key,
        startMs: parseStageDay(group.parent.planned_date_start),
        endMs,
        isCurrent: false,
        isTotal: false,
        tier: 'contract' as const,
        barOverdue: endMs != null && endMs < todayMs,
        extraBars,
        rowSpan: 1 + group.extensions.length,
      };
    });
    const marked = rows
      .flatMap((row) => [row.startMs, row.endMs, ...(row.extraBars ?? []).flatMap((bar) => [bar.startMs, bar.endMs])])
      .filter((ms): ms is number => ms != null);
    const rawStartMs = marked.length > 0 ? Math.min(...marked) : todayMs;
    const rawEndMs = marked.length > 0 ? Math.max(...marked) : todayMs;
    const paddedStart = new Date(rawStartMs);
    paddedStart.setMonth(paddedStart.getMonth() - 1);
    const paddedEnd = new Date(rawEndMs);
    paddedEnd.setMonth(paddedEnd.getMonth() + 1);
    const starts = stageDrafts.map((row) => row.planned_date_start.trim()).filter(Boolean).sort();
    const ends = stageDrafts.map((row) => row.planned_date_end.trim()).filter(Boolean).sort();
    return {
      rows,
      spanStartMs: paddedStart.getTime(),
      spanEndMs: paddedEnd.getTime(),
      axisStartLabel: starts[0] ? formatDateShort(starts[0]) : '—',
      axisEndLabel: ends.length > 0 ? formatDateShort(ends[ends.length - 1]) : '—',
      todayMs,
      totalOverdue: rows.some(
        (row) => row.barOverdue === true || (row.extraBars ?? []).some((bar) => bar.barOverdue === true),
      ),
    };
  }, [cardEditing, stageDrafts]);

  const familyTotalAmount = useMemo(() => sumFamilyAmount(familyContracts), [familyContracts]);

  const paymentPaid = useMemo(() => {
    if (family && planfactOperations.length > 0) {
      return sumContractFamilyReceiptsFromOperations(planfactOperations, family.familyIds);
    }
    return contract?.payment_paid ?? 0;
  }, [family, planfactOperations, contract?.payment_paid]);

  const paymentRemainder = familyTotalAmount - paymentPaid;
  const paymentPercent =
    familyTotalAmount > 0 ? Math.round((paymentPaid / familyTotalAmount) * 100) : 0;

  const familyActualExpenses = useMemo(() => {
    if (planfactOperations.length > 0 && familyTasks.length > 0) {
      return sumFamilyTaskActualExpensesFromOperations(planfactOperations, familyTasks);
    }
    return contract?.family_actual_expenses ?? 0;
  }, [planfactOperations, familyTasks, contract?.family_actual_expenses]);

  const familyTaskReceipts = useMemo(() => {
    if (planfactOperations.length > 0 && familyTasks.length > 0) {
      return sumFamilyTaskReceiptsFromOperations(planfactOperations, familyTasks);
    }
    return contract?.family_task_receipts ?? 0;
  }, [planfactOperations, familyTasks, contract?.family_task_receipts]);

  const expenseExtras = useMemo(
    () => ({
      cash: canOutcomeCash,
      tax: canOutcomeTax,
      serviceFee: canOutcomeServ,
      partnerCommission: canOutcomePartner,
    }),
    [canOutcomeCash, canOutcomeTax, canOutcomeServ, canOutcomePartner],
  );

  const canViewAllExpenseArticles = useMemo((): boolean => {
    if (hasDepartment('ALL_COMP')) {
      return true;
    }
    if (user == null) {
      return false;
    }
    const managerId = contract?.manager_id ?? null;
    const projectLeadId = contract?.project_lead_id ?? null;
    return user.id === managerId || user.id === projectLeadId;
  }, [hasDepartment, user, contract?.manager_id, contract?.project_lead_id]);

  const expenseArticleFilter = useMemo((): Set<string> | null => {
    if (canViewAllExpenseArticles) {
      return null;
    }
    return new Set(expensePlanfactArticles);
  }, [canViewAllExpenseArticles, expensePlanfactArticles]);

  const visibleActualExpenses = useMemo(() => {
    const uniqueTaskIds = [
      ...new Set(familyTasks.map((task) => task.task_id.trim()).filter(Boolean)),
    ];
    if (uniqueTaskIds.length === 0 || planfactOperations.length === 0) {
      return familyActualExpenses;
    }
    let total = 0;
    for (const taskId of uniqueTaskIds) {
      const table = buildTaskExpensesTable({
        operations: planfactOperations,
        articles,
        businessTaskId: taskId,
        extras: expenseExtras,
        allowedPlanfactArticles: expenseArticleFilter,
      });
      total += table.rows.find((row) => row.id === TASK_BALANCE_EXPENSES_ROW_ID)?.values.total ?? 0;
    }
    return total;
  }, [
    familyTasks,
    planfactOperations,
    articles,
    expenseExtras,
    expenseArticleFilter,
    familyActualExpenses,
  ]);

  const budgetPlan = useMemo(() => sumTasksPlannedExpenses(familyTasks), [familyTasks]);
  const budgetSpent = visibleActualExpenses;
  const budgetRemainder = budgetPlan != null ? budgetPlan + visibleActualExpenses : null;
  const balanceValue = familyTaskReceipts + familyActualExpenses;
  /** Баланс вкладки: поступления по сделке (deal_name), расходы по ID задач. */
  const balanceTabReceipts = paymentPaid;
  const balanceTabValue = balanceTabReceipts + familyActualExpenses;

  const maxEndDate = useMemo(() => maxFamilyDate(familyContracts, 'end_date'), [familyContracts]);
  const expenseClosingSummary = useMemo(
    () => resolveFamilyExpenseClosingSummary(familyTasks),
    [familyTasks],
  );
  const stageClosingSummary = useMemo(
    () => resolveFamilyStageClosingSummary(workStages),
    [workStages],
  );
  const stageReferenceDate = useMemo(
    () => resolveFamilyStageReferenceDate(workStages),
    [workStages],
  );
  const deadline = useMemo(
    () => formatDeadlineRelativeTo(maxEndDate, stageReferenceDate),
    [maxEndDate, stageReferenceDate],
  );

  const statusChecks = useMemo(
    () => [
      {
        key: 'signed',
        label: 'все договоры подписаны',
        done: familyContracts.length > 0 && familyContracts.every((ct) => Boolean(ct.has_original)),
      },
      {
        key: 'paid',
        label: 'полная оплата',
        done: familyTotalAmount > 0 && paymentRemainder <= 0,
      },
      {
        key: 'stagesClosed',
        label: 'все этапы закрыты',
        done: stageClosingSummary.allClosed,
      },
      {
        key: 'tasksClosed',
        label: 'все задачи закрыты',
        done: expenseClosingSummary.allClosed,
      },
    ],
    [familyContracts, familyTotalAmount, paymentRemainder, stageClosingSummary.allClosed, expenseClosingSummary.allClosed],
  );

  const contractsById = useMemo(() => {
    const map = new Map<number, Contract>();
    for (const row of allContracts) {
      map.set(row.id, row);
    }
    return map;
  }, [allContracts]);

  const amountMismatchInfo = useMemo(
    () => resolveContractTasksAmountMismatch(familyTotalAmount, familyTasks),
    [familyTotalAmount, familyTasks],
  );

  const marginFinanceRows = useMemo(
    () =>
      canBudgetTab
        ? buildMarginFinanceRows({ canSum: true, canCost: true, canMargin: true })
        : [],
    [canBudgetTab],
  );

  const marginColumnTasks = useMemo(
    () => sortMarginCalcColumns(familyTasks, contractsById, workTypeLabel),
    [familyTasks, contractsById, workTypeLabel],
  );

  const marginHeaderCells = useMemo(
    () => buildMarginCalcHeaderCells(marginColumnTasks, contractsById, workTypeLabel),
    [marginColumnTasks, contractsById, workTypeLabel],
  );

  const marginTableData = useMemo(() => {
    const valueForColumn = buildReadOnlyMarginValueGetter(marginFinanceRows);
    return buildMarginCalcTableData({
      financeRows: marginFinanceRows,
      columnTasks: marginColumnTasks,
      valueForColumn,
    });
  }, [marginFinanceRows, marginColumnTasks]);

  const visibleTabs = useMemo((): Array<{ id: ContractCardTabId; label: string; warn?: boolean }> => {
    const tabs: Array<{ id: ContractCardTabId; label: string; warn?: boolean }> = [];
    tabs.push({ id: 'general', label: 'Основное' });
    tabs.push({ id: 'workSchedule', label: 'График работ' });
    if (canBudgetTab) {
      tabs.push({
        id: 'budget',
        label: 'Расчет маржи',
        warn: amountMismatchInfo.mismatch,
      });
    }
    if (canReceiptsTab) {
      tabs.push({ id: 'receipts', label: 'Поступления' });
    }
    if (canBalanceTab) {
      tabs.push({
        id: 'balance',
        label: 'Денежный Поток',
        warn: balanceTabValue < 0,
      });
    }
    tabs.push({
      id: 'deadlines',
      label: 'Сроки',
      warn: deadline.label != null && deadline.overdue,
    });
    return tabs;
  }, [
    canBudgetTab,
    canReceiptsTab,
    canBalanceTab,
    amountMismatchInfo.mismatch,
    balanceTabValue,
    deadline.label,
    deadline.overdue,
  ]);

  const familyTaskIds = useMemo(
    () => familyTasks.map((task) => task.task_id),
    [familyTasks],
  );

  const familyContractIds = useMemo(
    () => familyContracts.map((ct) => ct.id),
    [familyContracts],
  );

  const cashFlowExtras = ALL_TASK_EXPENSE_EXTRAS;

  const balanceTableData = useMemo(() => {
    if (!canBalanceTab || (familyContractIds.length === 0 && familyTaskIds.length === 0)) {
      return null;
    }
    return buildContractFamilyBalanceTable({
      operations: planfactOperations,
      articles,
      familyContractIds,
      familyTaskIds,
      extras: cashFlowExtras,
    });
  }, [canBalanceTab, planfactOperations, articles, familyContractIds, familyTaskIds, cashFlowExtras]);

  const familyOperations = useMemo(
    () => filterContractFamilyOperations(planfactOperations, familyContractIds, familyTaskIds),
    [planfactOperations, familyContractIds, familyTaskIds],
  );

  const displayedOperations = useMemo(() => {
    if (activeCardTab === 'receipts') {
      return familyOperations.filter((operation) => isOperationReceipt(operation));
    }
    if (opsViewFilter.kind === 'balanceCell') {
      return collectOperationsForContractFamilyBalanceCell({
        operations: planfactOperations,
        articles,
        familyContractIds,
        familyTaskIds,
        rowId: opsViewFilter.rowId,
        columnKey: opsViewFilter.columnKey,
        extras: cashFlowExtras,
      });
    }
    return excludeHiddenExpenseCategoryOperations(familyOperations, articles, cashFlowExtras);
  }, [
    activeCardTab,
    opsViewFilter,
    familyOperations,
    planfactOperations,
    articles,
    familyContractIds,
    familyTaskIds,
    cashFlowExtras,
  ]);

  const opsFilterHint = useMemo((): string | null => {
    if (activeCardTab === 'receipts') {
      return 'Статья бюджета · ДОХОДЫ';
    }
    if (opsViewFilter.kind === 'balanceCell') {
      return `${opsViewFilter.rowLabel} · ${opsViewFilter.columnLabel}`;
    }
    return null;
  }, [activeCardTab, opsViewFilter]);

  const handleBalanceCellClick = useCallback((cell: TaskBalanceCellClick): void => {
    setOpsViewFilter({
      kind: 'balanceCell',
      rowId: cell.rowId,
      rowLabel: cell.rowLabel,
      columnKey: cell.columnKey,
      columnLabel: cell.columnLabel,
    });
    window.requestAnimationFrame(() => {
      document.getElementById('contract-detail-operations')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, []);

  const clearOpsFilter = useCallback((): void => {
    setOpsViewFilter({ kind: 'all' });
  }, []);

  const showOperationsBlock =
    (activeCardTab === 'receipts' && canReceiptsTab)
    || (activeCardTab === 'balance' && canBalanceTab);

  const familyContractsForDeadlines = useMemo(() => {
    if (!cardEditing) return familyContracts;
    return familyContracts.map((ct) => {
      const draft = deadlineDrafts[ct.id];
      if (!draft) return ct;
      return {
        ...ct,
        start_date: draft.start_date,
        end_date: draft.end_date || undefined,
      };
    });
  }, [familyContracts, cardEditing, deadlineDrafts]);

  const contractDeadlinesData = useMemo(
    () =>
      buildContractStageDeadlinesData({
        familyContracts: familyContractsForDeadlines,
        familyStages: workStages,
        currentContractId: contract?.id,
      }),
    [familyContractsForDeadlines, workStages, contract?.id],
  );

  const visibleDeadlinesData = useMemo(() => {
    if (deadlinesTasksExpanded) return contractDeadlinesData;
    return {
      ...contractDeadlinesData,
      tableRows: contractDeadlinesData.tableRows.filter((row) => row.isContractRow || row.isTotal),
      ganttRows: contractDeadlinesData.ganttRows.filter((row) => row.tier === 'contract' || row.isTotal),
    };
  }, [contractDeadlinesData, deadlinesTasksExpanded]);

  const displayContractStatus = contract?.contract_status ?? '';

  const contractReceiptsData = useMemo(
    () => {
      const editingSums = cardEditing && canEditSum;
      const amountOverrides = editingSums
        ? Object.fromEntries(
            familyContracts.map((ct) => {
              const parsed = parseContractSumInput(amountDrafts[ct.id] ?? '');
              return [ct.id, parsed ?? null];
            }),
          )
        : undefined;
      return buildContractReceiptsTabData({
        familyContracts,
        planfactOperations,
        bankAccounts,
        bankAccountDrafts: editingSums ? bankAccountDrafts : Object.fromEntries(
          familyContracts.map((ct) => [ct.id, ct.bank_account_id ?? null]),
        ),
        amountDrafts: amountOverrides,
        currentContractId: contract?.id,
        displayStatus: displayContractStatus,
        familyFallbackPaid: paymentPaid,
      });
    },
    [
      familyContracts,
      planfactOperations,
      bankAccounts,
      bankAccountDrafts,
      amountDrafts,
      cardEditing,
      canEditSum,
      contract?.id,
      displayContractStatus,
      paymentPaid,
    ],
  );

  const familyPaymentNotes = useMemo(() => {
    const familyIds = new Set(familyContracts.map((ct) => ct.id));
    const notesById = new Map<number, ContractNote>();
    for (const ct of allContracts) {
      if (!familyIds.has(ct.id)) continue;
      for (const note of ct.payment_notes ?? []) {
        notesById.set(note.id, note);
      }
    }
    for (const note of contract?.payment_notes ?? []) {
      notesById.set(note.id, note);
    }
    return [...notesById.values()];
  }, [familyContracts, allContracts, contract?.payment_notes]);

  const handlePaymentNoteAdded = useCallback((note: ContractNote): void => {
    const appendNote = (notes: ContractNote[] | undefined): ContractNote[] => {
      const list = notes ?? [];
      if (list.some((row) => row.id === note.id)) return list;
      return [note, ...list];
    };
    setContract((prev) =>
      prev?.id === note.contract_id
        ? { ...prev, payment_notes: appendNote(prev.payment_notes) }
        : prev,
    );
    setAllContracts((prev) =>
      prev.map((row) =>
        row.id === note.contract_id
          ? { ...row, payment_notes: appendNote(row.payment_notes) }
          : row,
      ),
    );
  }, []);

  const closePaymentNotes = useCallback((): void => {
    setPaymentNotesOpen(false);
  }, []);

  useEffect(() => {
    if (!paymentNotesOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        closePaymentNotes();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [paymentNotesOpen, closePaymentNotes]);

  useEffect(() => {
    if (activeCardTab !== 'receipts') {
      setPaymentNotesOpen(false);
    }
  }, [activeCardTab]);

  useEffect(() => {
    if (!visibleTabs.some((t) => t.id === activeCardTab)) {
      setActiveCardTab(visibleTabs[0]?.id ?? 'general');
    }
  }, [visibleTabs, activeCardTab]);

  const saveHeaderField = useCallback(
    async (patch: { contract_number?: string }): Promise<void> => {
      if (!contract || !canEditContract) return;
      setHeaderSaving(true);
      setHeaderError('');
      try {
        const updated = await updateContract(contract.id, patch);
        setContract((prev) => ({
          ...updated,
          payment_paid: prev?.payment_paid,
          family_task_receipts: prev?.family_task_receipts,
          family_actual_expenses: prev?.family_actual_expenses,
        }));
        setHeaderNumber(updated.contract_number);
        const idx = allContracts.findIndex((c) => c.id === updated.id);
        if (idx >= 0) {
          const next = [...allContracts];
          next[idx] = updated;
          setAllContracts(next);
        }
      } catch (e) {
        setHeaderError(e instanceof Error ? e.message : 'Не удалось сохранить');
        setHeaderNumber(contract.contract_number);
      } finally {
        setHeaderSaving(false);
      }
    },
    [contract, canEditContract, allContracts],
  );

  const handleNumberBlur = useCallback((): void => {
    if (!contract) return;
    const trimmed = headerNumber.trim();
    if (trimmed === contract.contract_number) return;
    if (!trimmed) {
      setHeaderNumber(contract.contract_number);
      return;
    }
    if (/\s/.test(trimmed)) {
      setHeaderError('Номер договора не должен содержать пробелы');
      setHeaderNumber(contract.contract_number);
      return;
    }
    void saveHeaderField({ contract_number: trimmed });
  }, [contract, headerNumber, saveHeaderField]);

  const applyContractUpdate = useCallback(
    (updated: Contract): void => {
      setAllContracts((prev) => {
        const idx = prev.findIndex((c) => c.id === updated.id);
        if (idx < 0) return prev;
        const next = [...prev];
        next[idx] = updated;
        return next;
      });
      setContract((prev) => {
        if (prev?.id !== updated.id) return prev;
        return {
          ...updated,
          payment_paid: prev.payment_paid,
          family_task_receipts: prev.family_task_receipts,
          family_actual_expenses: prev.family_actual_expenses,
        };
      });
    },
    [],
  );

  const handleStartCardEdit = useCallback((): void => {
    if (!canEditContract && !canEditSum && !canEditSumStages) return;
    syncCardDrafts();
    setCardEditing(true);
    setCardError('');
    setActiveCardTab((prev) => (CARD_EDIT_TAB_IDS.includes(prev) ? prev : 'general'));
  }, [canEditContract, canEditSum, canEditSumStages, syncCardDrafts]);

  const handleCancelCardEdit = useCallback((): void => {
    syncCardDrafts();
    setCardEditing(false);
    setCardError('');
    setCalendarEpoch((prev) => prev + 1);
  }, [syncCardDrafts]);

  const handleSaveCardEdit = useCallback(async (): Promise<void> => {
    if (!canEditContract && !canEditSum && !canEditSumStages) return;

    if (canEditContract) {
      const stageError = validateStageDrafts(stageDrafts, { contracts: stageContractOptions });
      if (stageError) {
        setCardError(stageError);
        setActiveCardTab('workSchedule');
        return;
      }

      for (const ct of familyContracts) {
        const trimmed = (subjectDrafts[ct.id] ?? '').trim();
        if (!trimmed) {
          setCardError(`Укажите предмет договора ${ct.contract_number}`);
          setActiveCardTab('general');
          return;
        }
      }

      for (const ct of familyContracts) {
        if (ct.id === contract?.id) continue;
        const draft = deadlineDrafts[ct.id];
        const start = draft?.start_date.trim() ?? '';
        if (!start) {
          setCardError(`Укажите дату начала для договора ${ct.contract_number}`);
          setActiveCardTab('deadlines');
          return;
        }
      }
    }

    if (canEditSum && canReceiptsTab) {
      for (const ct of familyContracts) {
        const parsed = parseContractSumInput(amountDrafts[ct.id] ?? '');
        if (parsed == null) {
          setCardError(`Укажите сумму договора ${ct.contract_number}`);
          setActiveCardTab('receipts');
          return;
        }
      }
    }

    setCardSaving(true);
    setCardError('');
    try {
      if (canEditSumStages && canReceiptsTab) {
        try {
          for (const persist of calendarPersistersRef.current.values()) {
            await persist();
          }
        } catch (e) {
          setActiveCardTab('receipts');
          throw e;
        }
      }

      if (canEditContract && contract) {
        const keptStageIds = new Set(
          stageDrafts
            .map((row) => row.id)
            .filter((stageId): stageId is number => stageId != null),
        );
        if (canTasksEdit) {
          for (const task of contractTasks) {
            const desired = (taskStageDrafts[task.contracts_task_id] ?? []).filter((stageId) =>
              keptStageIds.has(stageId),
            );
            const current = task.contract_stage_ids ?? [];
            if (sameIdList(desired, current)) continue;
            const updated = await updateTask(task.id, {
              contract_stage_ids: desired,
            });
            setAllTasks((prev) =>
              prev.map((row) =>
                row.contracts_task_id === updated.contracts_task_id ? { ...row, ...updated } : row,
              ),
            );
          }
        }
        const savedStages = await replaceContractStages(contract.id, stagesToWrite(stageDrafts), { family: true });
        setWorkStages(savedStages);
        const refreshed = await getContract(contract.id);
        applyContractUpdate(refreshed);
      }

      for (const ct of familyContracts) {
        const trimmed = (subjectDrafts[ct.id] ?? '').trim();
        const stored = (ct.contract_subject ?? '').trim();
        const draft = deadlineDrafts[ct.id];
        const start = draft?.start_date.trim() ?? '';
        const end = draft?.end_date.trim() ?? '';
        const storedStart = ct.start_date?.trim() ?? '';
        const storedEnd = ct.end_date?.trim() ?? '';
        const draftBankAccountId = bankAccountDrafts[ct.id] ?? null;
        const storedBankAccountId = ct.bank_account_id ?? null;
        const draftAmount = parseContractSumInput(amountDrafts[ct.id] ?? '');
        const storedAmount = Number(ct.total_amount) || 0;

        const subjectChanged = canEditContract && trimmed !== stored;
        const datesChanged =
          canEditContract
          && ct.id !== contract?.id
          && (start !== storedStart || end !== storedEnd);
        const hasOriginalChanged =
          canEditContract && Boolean(hasOriginalDrafts[ct.id]) !== Boolean(ct.has_original);
        const bankAccountChanged =
          canEditSum && draftBankAccountId !== storedBankAccountId;
        const amountChanged =
          canEditSum && draftAmount != null && draftAmount !== storedAmount;
        if (
          !subjectChanged
          && !datesChanged
          && !hasOriginalChanged
          && !bankAccountChanged
          && !amountChanged
        ) {
          continue;
        }

        const patch: {
          contract_subject?: string;
          start_date?: string;
          end_date?: string;
          has_original?: boolean;
          bank_account_id?: number | null;
          total_amount?: number;
        } = {};
        if (subjectChanged) patch.contract_subject = trimmed;
        if (datesChanged) {
          patch.start_date = start;
          patch.end_date = end || undefined;
        }
        if (hasOriginalChanged) patch.has_original = Boolean(hasOriginalDrafts[ct.id]);
        if (bankAccountChanged) patch.bank_account_id = draftBankAccountId;
        if (amountChanged && draftAmount != null) patch.total_amount = draftAmount;
        const updated = await updateContract(ct.id, patch);
        applyContractUpdate(updated);
      }

      if (canEditContract && contract) {
        const leadersChanged =
          contractorDraftId !== contract.contractor_id
          || (managerDraftId ?? null) !== (contract.manager_id ?? null)
          || (projectLeadDraftId ?? null) !== (contract.project_lead_id ?? null);
        const sourcesChanged =
          (sourceDraftId ?? null) !== (contract.source_id ?? null)
          || (canEditPrimarySource
            && (primarySourceDraftId ?? null) !== (contract.primary_source_id ?? null));
        if (leadersChanged || sourcesChanged) {
          const patch: {
            contractor_id?: number;
            manager_id?: number;
            project_lead_id?: number;
            source_id?: number | null;
            primary_source_id?: number | null;
          } = {};
          if (leadersChanged) {
            patch.contractor_id = contractorDraftId ?? contract.contractor_id;
            patch.manager_id = managerDraftId ?? undefined;
            patch.project_lead_id = projectLeadDraftId ?? undefined;
          }
          if (sourcesChanged) {
            patch.source_id = sourceDraftId;
            if (canEditPrimarySource) {
              patch.primary_source_id = primarySourceDraftId;
            }
          }
          const updated = await updateContract(contract.id, patch);
          applyContractUpdate(updated);
          if (canContractors && updated.contractor_id !== contract.contractor_id) {
            try {
              setContractor(await getContractor(updated.contractor_id));
            } catch {
              setContractor(null);
            }
          }
        }
      }

      if (canEditContract && mainContract) {
        const addressTrim = addressDraft.trim();
        const cadastralTrim = cadastralDraft.trim();
        const storedAddress = (mainContract.address ?? '').trim();
        const storedCadastral = (mainContract.cadastral_number ?? '').trim();
        if (addressTrim !== storedAddress || cadastralTrim !== storedCadastral) {
          const updated = await updateContract(mainContract.id, {
            address: addressTrim || undefined,
            cadastral_number: cadastralTrim || undefined,
          });
          applyContractUpdate(updated);
        }
      }

      setCardEditing(false);
      syncCardDrafts();
    } catch (e) {
      setCardError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setCardSaving(false);
    }
  }, [
    canEditContract,
    canEditSum,
    canEditSumStages,
    canReceiptsTab,
    familyContracts,
    subjectDrafts,
    hasOriginalDrafts,
    deadlineDrafts,
    bankAccountDrafts,
    amountDrafts,
    contract,
    contractorDraftId,
    managerDraftId,
    projectLeadDraftId,
    sourceDraftId,
    primarySourceDraftId,
    canEditPrimarySource,
    mainContract,
    addressDraft,
    cadastralDraft,
    canContractors,
    applyContractUpdate,
    syncCardDrafts,
    stageDrafts,
    canTasksEdit,
    contractTasks,
    taskStageDrafts,
    stageContractOptions,
  ]);

  const handleStageBoundTasksChange = useCallback(
    (stageId: number, taskIds: string[]): void => {
      const selected = new Set(taskIds.map((id) => Number(id)).filter((id) => Number.isFinite(id)));
      setTaskStageDrafts((prev) => {
        const next: Record<number, number[]> = { ...prev };
        for (const task of contractTasks) {
          const current = new Set(next[task.contracts_task_id] ?? []);
          if (selected.has(task.contracts_task_id)) current.add(stageId);
          else current.delete(stageId);
          next[task.contracts_task_id] = [...current];
        }
        return next;
      });
      if (cardError) setCardError('');
    },
    [contractTasks, cardError],
  );

  const handleStageDraftsChange = useCallback(
    (next: StageDraft[]): void => {
      setStageDrafts(next);
      const kept = new Set(
        next.map((row) => row.id).filter((stageId): stageId is number => stageId != null),
      );
      setTaskStageDrafts((prev) => {
        const updated: Record<number, number[]> = {};
        for (const [key, ids] of Object.entries(prev)) {
          updated[Number(key)] = ids.filter((stageId) => kept.has(stageId));
        }
        return updated;
      });
      if (cardError) setCardError('');
    },
    [cardError],
  );

  const updateDeadlineDraft = useCallback(
    (contractId: number, field: keyof ContractDeadlineDraft, value: string): void => {
      setDeadlineDrafts((prev) => ({
        ...prev,
        [contractId]: {
          start_date: prev[contractId]?.start_date ?? '',
          end_date: prev[contractId]?.end_date ?? '',
          [field]: value,
        },
      }));
      if (cardError) setCardError('');
    },
    [cardError],
  );

  const updateBankAccountDraft = useCallback(
    (contractId: number, value: number | null): void => {
      setBankAccountDrafts((prev) => ({
        ...prev,
        [contractId]: value,
      }));
      if (cardError) setCardError('');
    },
    [cardError],
  );

  const updateAmountDraft = useCallback(
    (contractId: number, value: string): void => {
      setAmountDrafts((prev) => ({
        ...prev,
        [contractId]: normalizeContractSumDraft(value),
      }));
      if (cardError) setCardError('');
    },
    [cardError],
  );

  const toggleDeadlinesTasks = (): void => {
    setDeadlinesTasksExpanded((prev) => !prev);
  };

  const toggleContract = (contractId: number): void => {
    setExpandedContractIds((prev) => {
      const next = new Set(prev);
      if (next.has(contractId)) next.delete(contractId);
      else next.add(contractId);
      return next;
    });
  };

  const toggleReceiptsCalendar = (contractId: number): void => {
    setExpandedReceiptsIds((prev) => {
      const next = new Set(prev);
      if (next.has(contractId)) next.delete(contractId);
      else next.add(contractId);
      return next;
    });
    setMountedReceiptsIds((prev) => {
      if (prev.has(contractId)) return prev;
      const next = new Set(prev);
      next.add(contractId);
      return next;
    });
  };

  const registerCalendarPersist = useCallback(
    (contractId: number, persist: (() => Promise<void>) | null): void => {
      if (persist) calendarPersistersRef.current.set(contractId, persist);
      else calendarPersistersRef.current.delete(contractId);
    },
    [],
  );

  const contractorSelectOptions = useMemo(
    () => [...allContractors].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [allContractors],
  );

  const managerSelectOptions = useMemo(
    () => employeesForRoleSelect(managerEmployees, managerDraftId, employees),
    [managerEmployees, managerDraftId, employees],
  );

  const projectLeadSelectOptions = useMemo(
    () => employeesForRoleSelect(projectLeadEmployees, projectLeadDraftId, employees),
    [projectLeadEmployees, projectLeadDraftId, employees],
  );

  const sourceNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const source of contractSourcesLevel2) {
      map.set(source.id, source.name);
    }
    for (const source of contractSourcesLevel1) {
      map.set(source.id, source.name);
    }
    return map;
  }, [contractSourcesLevel2, contractSourcesLevel1]);

  if (!canOpenContractCard) {
    return (
      <div className="data-page">
        <div className="empty-state">
          Нет доступа к карточке договора (нужна роль ОП_Менеджер / ОУП_Рук. проекта или отдел ALL_COMP).
        </div>
      </div>
    );
  }

  if (!canContracts) {
    return (
      <div className="data-page">
        <div className="empty-state">Нет доступа к модулю договоров (требуется право VIEW).</div>
      </div>
    );
  }

  if (Number.isNaN(contractPk)) {
    return (
      <div className="data-page">
        <div className="error-message">Некорректный идентификатор договора</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="data-page">
        <div className="loading">Загрузка...</div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="data-page task-detail">
        <div className="error-message">{error || 'Договор не найден'}</div>
      </div>
    );
  }

  const managerName = getEmployeeName(contract.manager_id);
  const projectLeadName = getEmployeeName(contract.project_lead_id);
  const contractSourceName =
    contract.source_id != null ? sourceNameById.get(contract.source_id) ?? '—' : '—';
  const primarySourceName =
    contract.primary_source_id != null
      ? sourceNameById.get(contract.primary_source_id) ?? '—'
      : '—';
  const cardFieldsEditable = canEditContract && cardEditing;
  const receiptsSumFieldsEditable = canEditSum && cardEditing;
  const receiptsStagesEditable = canEditSumStages && cardEditing;
  const canStartCardEdit = canEditContract || canEditSum || canEditSumStages;

  return (
    <div className="task-detail contract-detail">
      <header className="task-detail__sticky">
        <div className="task-detail__header-main">
          {canEditContract ? (
            <input
              type="text"
              className="contractor-detail__name-field task-detail__name-field"
              value={headerNumber}
              onChange={(e) => {
                setHeaderNumber(e.target.value);
                setHeaderError('');
              }}
              onBlur={() => handleNumberBlur()}
              disabled={headerSaving}
              aria-label="Номер договора"
            />
          ) : (
            <h1 className="task-detail__title">{contract.contract_number}</h1>
          )}
          {headerSaving ? (
            <span className="contractor-detail__inline-hint" aria-live="polite">
              Сохранение…
            </span>
          ) : null}
        </div>
        {headerError ? <div className="contractor-detail__name-error">{headerError}</div> : null}

        <div className="task-detail__stats task-detail__stats--6">
          <div className="task-detail__stat">
            <div className="task-detail__stat-title">Основная информация</div>
            <div className="task-detail__stat-body">
              {canContractors && contractor ? (
                <button
                  type="button"
                  className="task-detail__link contract-detail__contractor-value"
                  title={contractor.name}
                  onClick={() => navigate(`/contractors/${contractor.id}`)}
                >
                  <span className="contract-detail__contractor-value-text">{contractor.name}</span>
                </button>
              ) : (
                <div className="contract-detail__contractor-value" title={contractor?.name ?? undefined}>
                  <span className="contract-detail__contractor-value-text">{contractor?.name ?? '—'}</span>
                </div>
              )}
              <div className="task-detail__stat-kv">Менеджер: {managerName}</div>
              <div className="task-detail__stat-kv">РП: {projectLeadName}</div>
            </div>
          </div>

          <div className="task-detail__stat contract-detail__stat--hierarchy">
            <div className="task-detail__stat-title">Иерархия договоров</div>
            <div className="task-detail__stat-body">
              <ul className="contract-detail__family-list">
                {familyContracts.map((ct) => {
                  const isCurrent = ct.id === contract.id;
                  const isLevel2 = ct.hierarchy_level === 2;
                  return (
                    <li
                      key={ct.id}
                      className={`contract-detail__family-item${isCurrent ? ' contract-detail__family-item--current' : ''}`}
                    >
                      <span
                        className={`contract-detail__family-sign${ct.has_original ? ' contract-detail__family-sign--ok' : ' contract-detail__family-sign--no'}`}
                        aria-hidden
                      >
                        {ct.has_original ? '✓' : '✗'}
                      </span>
                      {isLevel2 ? (
                        <span className="contract-detail__family-tree-mark" aria-hidden>
                          └──{' '}
                        </span>
                      ) : null}
                      <span className="contract-detail__family-num">{ct.contract_number}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="task-detail__stat-meta contract-detail__family-legend">
                <div>
                  <span className="contract-detail__family-sign contract-detail__family-sign--ok" aria-hidden>
                    ✓
                  </span>{' '}
                  подписан
                </div>
                <div>
                  <span className="contract-detail__family-sign contract-detail__family-sign--no" aria-hidden>
                    ✗
                  </span>{' '}
                  не подписан
                </div>
              </div>
            </div>
          </div>

          {canReceiptsTab ? (
            <div className="task-detail__stat">
              <div className="task-detail__stat-title">Остаток по поступлениям</div>
              <div className="task-detail__stat-body">
                <div className="task-detail__stat-row">
                  <span className="task-detail__stat-value">{formatContractSumDisplay(paymentRemainder)}</span>
                  <PaymentDonut percent={paymentPercent} tone="success" />
                </div>
                <div className="task-detail__stat-meta">
                  <div>всего: {formatContractSumDisplay(familyTotalAmount)}</div>
                  <div>поступило: {formatContractSumDisplay(paymentPaid)}</div>
                </div>
              </div>
            </div>
          ) : null}

          {canOutcome ? (
            <div className="task-detail__stat">
              <div className="task-detail__stat-title">Расходы</div>
              <div className="task-detail__stat-body">
                <div className="task-detail__stat-row">
                  {canOutcomePlan ? (
                    <>
                      <span
                        className={`task-detail__stat-value${
                          budgetRemainder != null && budgetRemainder > 0
                            ? ' task-detail__stat-value--positive'
                            : budgetRemainder != null && budgetRemainder < 0
                              ? ' task-detail__stat-value--negative'
                              : ''
                        }`}
                      >
                        {budgetRemainder != null ? formatSignedBalance(budgetRemainder) : '—'}
                      </span>
                      <BudgetProgressBar plan={budgetPlan} remainder={budgetRemainder} />
                    </>
                  ) : (
                    <span className="task-detail__stat-value">
                      {formatFactExpensesSum(budgetSpent)}
                    </span>
                  )}
                </div>
                <div className="task-detail__stat-meta">
                  {canOutcomePlan ? <div>план расходы: {formatFinanceCell(budgetPlan)}</div> : null}
                  <div>факт. расходы: {formatFactExpensesSum(budgetSpent)}</div>
                </div>
              </div>
            </div>
          ) : null}

          {canCashFlow ? (
            <div className="task-detail__stat">
              <div className="task-detail__stat-title">Денежный Поток</div>
              <div className="task-detail__stat-body">
                <div className="task-detail__stat-row">
                  <span
                    className={`task-detail__stat-value${
                      balanceValue > 0
                        ? ' task-detail__stat-value--positive'
                        : balanceValue < 0
                          ? ' task-detail__stat-value--negative'
                          : ''
                    }`}
                  >
                    {formatSignedBalance(balanceValue)}
                  </span>
                  <BalanceBarChart receipts={familyTaskReceipts} expenses={familyActualExpenses} />
                </div>
                <div className="task-detail__stat-meta">
                  <div>поступления: {formatContractSumDisplay(familyTaskReceipts)}</div>
                  <div>факт. расходы: {formatFactExpensesSum(familyActualExpenses)}</div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="task-detail__stat task-detail__stat--deadline">
            <div className="task-detail__stat-title">Сроки</div>
            <div className="task-detail__stat-body">
              {deadline.label ? (
                <div
                  className={`task-detail__deadline-heading${deadline.overdue ? ' task-detail__deadline-heading--overdue' : ''}`}
                >
                  {deadline.label}:
                </div>
              ) : null}
              <div
                className={`task-detail__deadline-counters${deadline.overdue ? ' task-detail__deadline-counters--overdue' : ''}`}
              >
                <div className="task-detail__deadline-unit">
                  <span className="task-detail__deadline-num">
                    {deadline.months != null ? deadline.months : '—'}
                  </span>
                  <span className="task-detail__deadline-label">мес.</span>
                </div>
                <div className="task-detail__deadline-unit">
                  <span className="task-detail__deadline-num">{deadline.days != null ? deadline.days : '—'}</span>
                  <span className="task-detail__deadline-label">дн.</span>
                </div>
              </div>
              <div className="task-detail__stat-meta">
                <div>крайний срок: {maxEndDate ? formatDate(maxEndDate) : '—'}</div>
                <div>
                  закрытие этапов:{' '}
                  {stageClosingSummary.allClosed && stageClosingSummary.maxClosingDate
                    ? formatDate(stageClosingSummary.maxClosingDate)
                    : '—'}
                </div>
              </div>
            </div>
          </div>

          <div className="task-detail__stat task-detail__stat--closing">
            <div className="task-detail__stat-title">Статусы</div>
            <div className="task-detail__stat-body task-detail__stat-body--closing">
              {displayContractStatus ? (
                <span
                  className={`task-detail__status-badge task-detail__status-badge--closing task-detail__status-badge--${contractStatusBadgeVariant(displayContractStatus)}`}
                >
                  {displayContractStatus}
                </span>
              ) : null}
              <ul className="contract-detail__status-checks">
                {statusChecks.map((item) => (
                  <li key={item.key} className="contract-detail__status-check-row">
                    <StatusCheckIcon done={item.done} label={item.label} />
                    <span className="contract-detail__status-check-label">{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </header>

      <section className="contractor-detail__card task-detail__card">
        <div className={`contractor-detail__card-head${!canStartCardEdit ? ' contractor-detail__card-head--solo' : ''}`}>
          <h2 className="contractor-detail__card-title">Карточка договора</h2>
          {canStartCardEdit ? (
            cardEditing ? (
              <div className="contractor-detail__card-actions">
                <button
                  type="button"
                  className="submit-button"
                  disabled={cardSaving}
                  onClick={() => void handleSaveCardEdit()}
                >
                  {cardSaving ? 'Сохранение…' : 'Сохранить'}
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  disabled={cardSaving}
                  onClick={handleCancelCardEdit}
                >
                  Отмена
                </button>
              </div>
            ) : (
              <button type="button" className="secondary-button" onClick={handleStartCardEdit}>
                Редактировать
              </button>
            )
          ) : null}
        </div>
        {cardError ? <div className="contractor-detail__card-error">{cardError}</div> : null}

        <div className="contractor-detail__tabs" role="tablist">
          {visibleTabs.map((tab) => {
            const tabLockedInEdit = cardEditing && !CARD_EDIT_TAB_IDS.includes(tab.id);
            return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeCardTab === tab.id}
              disabled={tabLockedInEdit}
              title={
                tabLockedInEdit ? 'Сначала сохраните или отмените редактирование' : undefined
              }
              className={`contractor-detail__tab${activeCardTab === tab.id ? ' contractor-detail__tab--active' : ''}${tab.warn ? ' contractor-detail__tab--warn' : ''}${tabLockedInEdit ? ' contractor-detail__tab--disabled' : ''}`}
              onClick={() => {
                if (tabLockedInEdit) return;
                setActiveCardTab(tab.id);
              }}
            >
              {tab.warn ? (
                <span className="contract-detail__tab-warn-dot" aria-hidden>
                  !
                </span>
              ) : null}
              {tab.label}
            </button>
            );
          })}
        </div>

        {activeCardTab === 'receipts' && (
          <div className="contractor-detail__tab-panel" role="tabpanel">
            {familyContracts.length === 0 ? (
              <p className="contractor-detail__muted">Договоры семьи недоступны в вашей выборке.</p>
            ) : (
              <>
                <div
                  className={`contract-detail__deadlines-banner${
                    contractReceiptsData.summaryKind === 'debt'
                      ? ' contract-detail__deadlines-banner--overdue'
                      : contractReceiptsData.summaryKind === 'paid'
                        ? ' contract-detail__deadlines-banner--on-time'
                        : ' contract-detail__deadlines-banner--neutral'
                  }`}
                >
                  <div className="contract-detail__deadlines-banner-main">
                    {contractReceiptsData.summaryKind === 'paid' ? (
                      <div className="contract-detail__deadlines-banner-heading">
                        {contractReceiptsData.summaryLabel}
                      </div>
                    ) : (
                      <>
                        <div
                          className={`contract-detail__deadlines-banner-heading${
                            contractReceiptsData.summaryKind === 'debt'
                              ? ' contract-detail__deadlines-banner-heading--overdue'
                              : ''
                          }`}
                        >
                          {contractReceiptsData.summaryLabel}
                        </div>
                        <div
                          className={`contract-detail__deadlines-banner-counters contract-detail__receipts-banner-counters${
                            contractReceiptsData.summaryKind === 'debt'
                              ? ' contract-detail__deadlines-banner-counters--overdue'
                              : ''
                          }`}
                        >
                          <span className="contract-detail__receipts-banner-value">
                            {formatContractSumDisplay(contractReceiptsData.summaryAmount ?? 0)}
                          </span>
                          <span className="contract-detail__deadlines-banner-unit">руб.</span>
                          <span className="contract-detail__receipts-banner-value">
                            {contractReceiptsData.totals.paidPercent}%
                          </span>
                          <span className="contract-detail__deadlines-banner-unit">оплачено</span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="contract-detail__deadlines-banner-meta">
                    <span>Всего: {formatContractSumDisplay(contractReceiptsData.totals.totalAmount)}</span>
                    <span>Поступило: {formatContractSumDisplay(contractReceiptsData.totals.paidAmount)}</span>
                  </div>
                </div>
                <div className="task-detail__deadlines-layout contract-detail__receipts-layout">
                  <div className="contract-detail__receipts-main">
                  <table className="task-detail__mini-table task-detail__mini-table--compact contract-detail__receipts-table">
                    <thead>
                      <tr>
                        <th>Договор</th>
                        <th className="contract-detail__receipts-col-account">Счёт оплаты</th>
                        <th className="contract-detail__receipts-col-currency">Валюта</th>
                        <th className="contract-detail__receipts-col-num">Сумма</th>
                        <th className="contract-detail__receipts-col-num">Оплачено</th>
                        <th className="contract-detail__receipts-col-num">Остаток</th>
                        <th className="contract-detail__receipts-col-pct">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contractReceiptsData.tableRows.map((row) => {
                        const receiptsExpanded =
                          row.contractId != null && expandedReceiptsIds.has(row.contractId);
                        const calendarMounted =
                          !row.isTotal
                          && row.contractId != null
                          && (
                            receiptsExpanded
                            || (receiptsStagesEditable && mountedReceiptsIds.has(row.contractId))
                          );
                        return (
                          <React.Fragment key={row.key}>
                            <tr
                              className={row.isTotal ? 'task-detail__deadlines-row--total' : undefined}
                            >
                              <td className="task-detail__td-text">
                                {row.isTotal ? (
                                  <strong>{row.contractNumber}</strong>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      className="contract-detail__deadlines-expand-btn"
                                      onClick={() => toggleReceiptsCalendar(row.contractId!)}
                                      aria-expanded={receiptsExpanded}
                                      aria-label={
                                        receiptsExpanded
                                          ? `Скрыть график поступлений ${row.contractNumber}`
                                          : `Показать график поступлений ${row.contractNumber}`
                                      }
                                    >
                                      <span className="task-detail__chevron" aria-hidden>
                                        {receiptsExpanded ? '▼' : '▶'}
                                      </span>
                                    </button>
                                    {row.hierarchyLevel === 2 ? (
                                      <span className="contract-detail__family-tree-mark" aria-hidden>
                                        └──{' '}
                                      </span>
                                    ) : null}
                                    <strong>{row.contractNumber}</strong>
                                  </>
                                )}
                              </td>
                              <td className="contract-detail__receipts-col-account">
                                {receiptsSumFieldsEditable && row.contractId != null ? (
                                  <select
                                    className="contractor-detail__field contract-detail__receipts-account-select"
                                    value={row.bankAccountId == null ? '' : String(row.bankAccountId)}
                                    disabled={cardSaving}
                                    onChange={(e) => {
                                      updateBankAccountDraft(
                                        row.contractId!,
                                        e.target.value === '' ? null : Number(e.target.value),
                                      );
                                    }}
                                    aria-label={`Счёт оплаты по договору ${row.contractNumber}`}
                                  >
                                    <option value="">—</option>
                                    {bankAccounts.map((account) => (
                                      <option key={account.id} value={account.id}>
                                        {account.planfact_account}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  row.planfactAccount
                                )}
                              </td>
                              <td className="contract-detail__receipts-col-currency">{row.currencyType}</td>
                              <td className="contract-detail__receipts-col-num">
                                {receiptsSumFieldsEditable && row.contractId != null ? (
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="contractor-detail__field task-detail__cell-input task-detail__cell-input--num contract-detail__receipts-amount-input"
                                    value={displayContractSumDraft(amountDrafts[row.contractId] ?? '')}
                                    onChange={(e) => updateAmountDraft(row.contractId!, e.target.value)}
                                    disabled={cardSaving}
                                    aria-label={`Сумма договора ${row.contractNumber}`}
                                  />
                                ) : row.isTotal ? (
                                  <strong>{formatContractSumDisplay(row.totalAmount)}</strong>
                                ) : (
                                  formatContractSumDisplay(row.totalAmount)
                                )}
                              </td>
                              <td className="contract-detail__receipts-col-num">
                                {formatContractSumDisplay(row.paidAmount)}
                              </td>
                              <td className="contract-detail__receipts-col-num">
                                {row.isTotal ? (
                                  <strong>{formatContractSumDisplay(row.remainder)}</strong>
                                ) : (
                                  formatContractSumDisplay(row.remainder)
                                )}
                              </td>
                              <td className="contract-detail__receipts-col-pct">
                                <PaymentDonut
                                  percent={row.paidPercent}
                                  tone="success"
                                  className="contract-detail__receipts-donut"
                                />
                              </td>
                            </tr>
                            {calendarMounted && row.contractId != null ? (
                              <ContractPaymentCalendarTable
                                key={`${row.contractId}-${calendarEpoch}`}
                                contractId={row.contractId}
                                contractAmount={row.totalAmount}
                                paidAmount={row.paidAmount}
                                canEdit={receiptsStagesEditable}
                                disabled={cardSaving}
                                collapsed={!receiptsExpanded}
                                indentLevel={row.hierarchyLevel === 2 ? 2 : 1}
                                onRegisterPersist={registerCalendarPersist}
                              />
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                  <div className="contract-detail__receipts-side">
                    {canIncomePlanningView ? (
                      <button
                        type="button"
                        className="contract-detail__notes-open"
                        onClick={() => setPaymentNotesOpen(true)}
                      >
                        <span>Комментарии по оплатам</span>
                        {familyPaymentNotes.length > 0 ? (
                          <span className="contract-detail__notes-open-count">
                            {familyPaymentNotes.length}
                          </span>
                        ) : null}
                      </button>
                    ) : null}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeCardTab === 'balance' && (
          <div className="contractor-detail__tab-panel" role="tabpanel">
            <div
              className={`contract-detail__deadlines-banner${
                balanceTabValue < 0
                  ? ' contract-detail__deadlines-banner--overdue'
                  : ' contract-detail__deadlines-banner--on-time'
              }`}
            >
              <div className="contract-detail__deadlines-banner-main">
                <div
                  className={`contract-detail__deadlines-banner-heading${
                    balanceTabValue < 0 ? ' contract-detail__deadlines-banner-heading--overdue' : ''
                  }`}
                >
                  {balanceTabValue < 0
                    ? 'Расходы превышают поступления на'
                    : 'Поступления превышают расходы на'}
                </div>
                <div
                  className={`contract-detail__deadlines-banner-counters${
                    balanceTabValue < 0 ? ' contract-detail__deadlines-banner-counters--overdue' : ''
                  }`}
                >
                  <span className="contract-detail__deadlines-banner-num">
                    {formatContractSumDisplay(Math.abs(balanceTabValue))}
                  </span>
                  <span className="contract-detail__deadlines-banner-unit">руб.</span>
                </div>
              </div>
              <div className="contract-detail__deadlines-banner-meta">
                <span>поступления: {formatContractSumDisplay(balanceTabReceipts)}</span>
                <span>факт. расходы: {formatFactExpensesSum(familyActualExpenses)}</span>
              </div>
            </div>
            {!balanceTableData ? (
              <p className="contractor-detail__muted">Нет данных для баланса.</p>
            ) : (
              <TaskBalanceTable data={balanceTableData} onCellClick={handleBalanceCellClick} />
            )}
          </div>
        )}

        {activeCardTab === 'general' && (
          <div className="contractor-detail__tab-panel" role="tabpanel">
            <div className="contract-detail__general-layout">
              <div className="contract-detail__general-leaders-row">
                <div className="task-executors__block">
                  <div className="task-executors__block-head">
                    <span className="task-executors__dept-label">
                      <span className="task-executors__dept-name">Контрагент</span>
                    </span>
                  </div>
                  <div className="task-executors__list">
                    {cardFieldsEditable && canContractors ? (
                      <ContractorPicker
                        className="contract-detail__general-contractor-picker"
                        contractors={contractorSelectOptions}
                        value={contractorDraftId}
                        disabled={cardSaving}
                        onChange={(next) => {
                          setContractorDraftId(next);
                          if (cardError) setCardError('');
                        }}
                        ariaLabel="Контрагент"
                        emptyLabel="—"
                        placeholder="Введите название или ИНН контрагента"
                      />
                    ) : canContractors && contractor ? (
                      <button
                        type="button"
                        className="task-executors__person task-detail__link contract-detail__general-contractor"
                        onClick={() => navigate(`/contractors/${contractor.id}`)}
                      >
                        {contractor.name}
                      </button>
                    ) : (
                      <div className="task-executors__person">{contractor?.name ?? '—'}</div>
                    )}
                  </div>
                </div>
                <div className="task-executors__block">
                  <div className="task-executors__block-head">
                    <span className="task-executors__dept-label">
                      <span className="task-executors__dept-name">Менеджер</span>
                    </span>
                  </div>
                  <div className="task-executors__list">
                    {cardFieldsEditable ? (
                      <select
                        className="contractor-detail__field contract-detail__general-select"
                        value={managerDraftId == null ? '' : String(managerDraftId)}
                        disabled={cardSaving}
                        onChange={(e) => {
                          setManagerDraftId(e.target.value === '' ? null : Number(e.target.value));
                          if (cardError) setCardError('');
                        }}
                        aria-label="Менеджер"
                      >
                        <option value="">—</option>
                        {managerSelectOptions.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.full_name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="task-executors__person">{managerName}</div>
                    )}
                  </div>
                </div>
                <div className="task-executors__block">
                  <div className="task-executors__block-head">
                    <span className="task-executors__dept-label">
                      <span className="task-executors__dept-name">РП</span>
                    </span>
                  </div>
                  <div className="task-executors__list">
                    {cardFieldsEditable ? (
                      <select
                        className="contractor-detail__field contract-detail__general-select"
                        value={projectLeadDraftId == null ? '' : String(projectLeadDraftId)}
                        disabled={cardSaving}
                        onChange={(e) => {
                          setProjectLeadDraftId(e.target.value === '' ? null : Number(e.target.value));
                          if (cardError) setCardError('');
                        }}
                        aria-label="РП"
                      >
                        <option value="">—</option>
                        {projectLeadSelectOptions.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.full_name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="task-executors__person">{projectLeadName}</div>
                    )}
                  </div>
                </div>
                <div className="task-executors__block">
                  <div className="task-executors__block-head">
                    <span className="task-executors__dept-label">
                      <span className="task-executors__dept-name">Источник</span>
                    </span>
                  </div>
                  <div className="task-executors__list">
                    {cardFieldsEditable ? (
                      <SourcePicker
                        className="contract-detail__general-source-picker"
                        sources={contractSourcesLevel2}
                        value={sourceDraftId}
                        disabled={cardSaving}
                        onChange={(next) => {
                          setSourceDraftId(next);
                          if (cardError) setCardError('');
                        }}
                        ariaLabel="Источник"
                        emptyLabel="—"
                        placeholder="Поиск источника"
                      />
                    ) : (
                      <div className="task-executors__person">{contractSourceName}</div>
                    )}
                  </div>
                </div>
                <div className="task-executors__block">
                  <div className="task-executors__block-head">
                    <span className="task-executors__dept-label">
                      <span className="task-executors__dept-name">Первичный источник</span>
                    </span>
                  </div>
                  <div className="task-executors__list">
                    {cardFieldsEditable && canEditPrimarySource ? (
                      <SourcePicker
                        className="contract-detail__general-source-picker"
                        sources={contractSourcesLevel1}
                        value={primarySourceDraftId}
                        disabled={cardSaving}
                        onChange={(next) => {
                          setPrimarySourceDraftId(next);
                          if (cardError) setCardError('');
                        }}
                        ariaLabel="Первичный источник"
                        emptyLabel="—"
                        placeholder="Поиск первичного источника"
                      />
                    ) : (
                      <div className="task-executors__person">{primarySourceName}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="contract-detail__general-fields">
                <div className="task-executors__block contract-detail__general-block">
                  <div className="task-executors__block-head">
                    <span className="task-executors__dept-label">
                      <span className="task-executors__dept-name">Иерархия договора</span>
                    </span>
                  </div>
                  <div className="contract-detail__general-block-body">
                    {familyContracts.length === 0 ? (
                      <p className="task-detail__hint">Договоры семьи недоступны в вашей выборке.</p>
                    ) : (
                      <div className="task-detail__accordion task-detail__accordion--hierarchy contract-detail__subject-hierarchy">
                        <table className="contract-detail__general-table">
                          <tbody>
                            {familyContracts.map((ct) => {
                              const contractExpanded = expandedContractIds.has(ct.id);
                              const contractTasks = canTasks
                                ? familyTasks
                                    .filter((t) => t.contract_id === ct.id)
                                    .sort((a, b) =>
                                      workTypeLabel(a.work_type_id).localeCompare(
                                        workTypeLabel(b.work_type_id),
                                        'ru',
                                        { sensitivity: 'base' },
                                      ),
                                    )
                                : [];
                              const isCurrent = ct.id === contract.id;
                              const subjectValue = subjectDrafts[ct.id] ?? ct.contract_subject ?? '';
                              return (
                                <React.Fragment key={ct.id}>
                                  <tr
                                    className={`contract-row${isCurrent ? ' task-row--current' : ''}${
                                      canTasks ? '' : ' contract-detail__subject-hierarchy-row--static'
                                    }`}
                                    onClick={canTasks ? () => toggleContract(ct.id) : undefined}
                                  >
                                    <td className="contract-detail__general-table-label">
                                      {canTasks ? (
                                        <>
                                          <span className="task-detail__chevron" aria-hidden>
                                            {contractExpanded ? '▼' : '▶'}
                                          </span>{' '}
                                        </>
                                      ) : null}
                                      {ct.hierarchy_level === 2 ? (
                                        <span className="contract-detail__family-tree-mark" aria-hidden>
                                          └──{' '}
                                        </span>
                                      ) : null}
                                      <button
                                        type="button"
                                        className="task-detail__link"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!isCurrent) navigate(`/contracts/${ct.id}`);
                                        }}
                                      >
                                        <strong>{ct.contract_number}</strong>
                                      </button>
                                    </td>
                                    <td
                                      className="contract-detail__general-table-value"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {cardFieldsEditable ? (
                                        <textarea
                                          className="contract-detail__general-field"
                                          rows={2}
                                          value={subjectValue}
                                          onChange={(e) => {
                                            setSubjectDrafts((prev) => ({
                                              ...prev,
                                              [ct.id]: e.target.value,
                                            }));
                                            if (cardError) setCardError('');
                                          }}
                                          disabled={cardSaving}
                                          aria-label={`Предмет договора ${ct.contract_number}`}
                                        />
                                      ) : (
                                        <div className="contract-detail__general-field contract-detail__general-field--readonly">
                                          {ct.contract_subject?.trim() ? ct.contract_subject : '—'}
                                        </div>
                                      )}
                                    </td>
                                    <td
                                      className="contract-detail__general-table-original"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {cardFieldsEditable ? (
                                        <label className="contract-detail__general-original-label">
                                          <input
                                            type="checkbox"
                                            checked={hasOriginalDrafts[ct.id] ?? false}
                                            disabled={cardSaving}
                                            onChange={(e) => {
                                              setHasOriginalDrafts((prev) => ({
                                                ...prev,
                                                [ct.id]: e.target.checked,
                                              }));
                                              if (cardError) setCardError('');
                                            }}
                                          />
                                          <span>оригинал подписан</span>
                                        </label>
                                      ) : (
                                        <span
                                          className={`contract-detail__family-sign${ct.has_original ? ' contract-detail__family-sign--ok' : ' contract-detail__family-sign--no'}`}
                                          title={
                                            ct.has_original
                                              ? 'Оригинал подписан'
                                              : 'Оригинал не подписан'
                                          }
                                          aria-label={
                                            ct.has_original
                                              ? 'Оригинал подписан'
                                              : 'Оригинал не подписан'
                                          }
                                        >
                                          {ct.has_original ? '✓' : '✗'}
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                  {canTasks &&
                                    contractExpanded &&
                                    (contractTasks.length === 0 ? (
                                      <tr className="task-row" key={`${ct.id}-empty`}>
                                        <td
                                          colSpan={3}
                                          className="task-detail__muted-text task-detail__muted-text--italic"
                                        >
                                          Нет задач по договору
                                        </td>
                                      </tr>
                                    ) : (
                                      contractTasks.map((tk) => (
                                        <tr
                                          key={tk.contracts_task_id}
                                          className="task-row contract-detail__hierarchy-task-row"
                                          onClick={() => navigate(taskDetailPath(tk))}
                                        >
                                          <td colSpan={3}>
                                            <span className="contract-detail__hierarchy-task-label">
                                              {tk.task_id}: {workTypeLabel(tk.work_type_id)}
                                            </span>
                                          </td>
                                        </tr>
                                      ))
                                    ))}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
                <div className="task-executors__block contract-detail__general-block">
                  <div className="task-executors__block-head">
                    <span className="task-executors__dept-label">
                      <span className="task-executors__dept-name">Адрес</span>
                    </span>
                  </div>
                  <div className="contract-detail__general-block-body">
                    <table className="contract-detail__general-table">
                      <tbody>
                        <tr>
                          <th scope="row" className="contract-detail__general-table-label">
                            Адрес
                          </th>
                        <td className="contract-detail__general-table-value">
                          {cardFieldsEditable ? (
                            <textarea
                              className="contract-detail__general-field"
                              rows={2}
                              value={addressDraft}
                              onChange={(e) => {
                                setAddressDraft(e.target.value);
                                if (cardError) setCardError('');
                              }}
                              disabled={cardSaving}
                              aria-label="Адрес"
                            />
                          ) : (
                            <div className="contract-detail__general-field contract-detail__general-field--readonly">
                              {mainContract?.address?.trim() ? mainContract.address : '—'}
                            </div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th scope="row" className="contract-detail__general-table-label">
                          к.н.
                        </th>
                        <td className="contract-detail__general-table-value">
                          {cardFieldsEditable ? (
                            <textarea
                              className="contract-detail__general-field"
                              rows={2}
                              value={cadastralDraft}
                              onChange={(e) => {
                                setCadastralDraft(e.target.value);
                                if (cardError) setCardError('');
                              }}
                              disabled={cardSaving}
                              aria-label="Кадастровый номер"
                            />
                          ) : (
                            <div className="contract-detail__general-field contract-detail__general-field--readonly">
                              {mainContract?.cadastral_number?.trim() ? mainContract.cadastral_number : '—'}
                            </div>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              </div>
            </div>
          </div>
        )}

        {activeCardTab === 'deadlines' && (
          <div className="contractor-detail__tab-panel" role="tabpanel">
            {visibleDeadlinesData.tableRows.length === 0 ? (
              <p className="contractor-detail__muted">Нет данных о сроках.</p>
            ) : (
              <>
                <div
                  className={`contract-detail__deadlines-banner${deadline.overdue ? ' contract-detail__deadlines-banner--overdue' : deadline.label ? ' contract-detail__deadlines-banner--on-time' : ' contract-detail__deadlines-banner--neutral'}`}
                >
                  <div className="contract-detail__deadlines-banner-main">
                    {deadline.label ? (
                      <div
                        className={`contract-detail__deadlines-banner-heading${deadline.overdue ? ' contract-detail__deadlines-banner-heading--overdue' : ''}`}
                      >
                        {deadline.label}
                      </div>
                    ) : (
                      <div className="contract-detail__deadlines-banner-heading">Сроки</div>
                    )}
                    {deadline.label ? (
                      <div
                        className={`contract-detail__deadlines-banner-counters${deadline.overdue ? ' contract-detail__deadlines-banner-counters--overdue' : ''}`}
                      >
                        <span className="contract-detail__deadlines-banner-num">
                          {deadline.months != null ? deadline.months : '—'}
                        </span>
                        <span className="contract-detail__deadlines-banner-unit">мес.</span>
                        <span className="contract-detail__deadlines-banner-num">
                          {deadline.days != null ? deadline.days : '—'}
                        </span>
                        <span className="contract-detail__deadlines-banner-unit">дн.</span>
                      </div>
                    ) : null}
                  </div>
                  <div className="contract-detail__deadlines-banner-meta">
                    <span>Крайний срок: {maxEndDate ? formatDate(maxEndDate) : '—'}</span>
                    <span>
                      Закрытие этапов:{' '}
                      {contractDeadlinesData.familyExpenseClosingDisplay !== '—'
                        ? contractDeadlinesData.familyExpenseClosingDisplay
                        : 'не завершено'}
                    </span>
                  </div>
                </div>
                <p className="contract-detail__deadlines-summary">
                  {visibleDeadlinesData.summary.contractCount} дог.
                  {' · '}
                  {visibleDeadlinesData.summary.taskCount} этапов
                  {visibleDeadlinesData.summary.overdueTaskCount > 0 ? (
                    <>
                      {' · '}
                      <span className="contract-detail__deadlines-summary-warn">
                        {visibleDeadlinesData.summary.overdueTaskCount} с просрочкой
                      </span>
                    </>
                  ) : null}
                  {visibleDeadlinesData.summary.closedOnTimeCount > 0 ? (
                    <>
                      {' · '}
                      {visibleDeadlinesData.summary.closedOnTimeCount} закрыто в срок
                    </>
                  ) : null}
                </p>
                <div className="task-detail__deadlines-layout">
                <table className="task-detail__mini-table task-detail__mini-table--compact task-detail__deadlines-table task-detail__deadlines-table--levels task-detail__deadlines-table--grouped contract-detail__deadlines-table">
                  <thead>
                    <tr className="task-detail__deadlines-group-row">
                      <th colSpan={3} className="task-detail__deadlines-group-spacer" />
                      <th colSpan={2} className="task-detail__deadlines-group-head task-detail__deadlines-group-head--plan">
                        План
                      </th>
                      <th colSpan={2} className="task-detail__deadlines-group-head task-detail__deadlines-group-head--fact">
                        Факт
                      </th>
                    </tr>
                    <tr>
                      <th className="task-detail__deadlines-level-col task-detail__deadlines-level-col--1">
                        Договор
                      </th>
                      <th className="task-detail__deadlines-level-col task-detail__deadlines-level-col--2">
                        Этап
                      </th>
                      <th className="task-detail__deadlines-level-col task-detail__deadlines-level-col--3">
                        Продление
                      </th>
                      <th className="task-detail__deadlines-date-head">Начало</th>
                      <th className="task-detail__deadlines-date-head">Крайний срок</th>
                      <th className="task-detail__deadlines-date-head">Закрытие этапа</th>
                      <th className="task-detail__deadlines-date-head task-detail__deadlines-delta-head">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleDeadlinesData.tableRows.map((row) => (
                      <tr
                        key={row.key}
                        className={[
                          row.isTotal ? 'task-detail__deadlines-row--total' : '',
                          row.isContractRow ? 'task-detail__deadlines-row--contract' : '',
                          row.isTaskRow ? 'task-detail__deadlines-row--task' : '',
                          row.isCurrent ? 'task-detail__deadlines-row--current' : '',
                        ]
                          .filter(Boolean)
                          .join(' ') || undefined}
                      >
                        {row.isTotal ? (
                          <td
                            colSpan={3}
                            className="task-detail__deadlines-level-col task-detail__deadlines-level-col--3 task-detail__deadlines-total-label"
                          >
                            <strong>Итого</strong>
                          </td>
                        ) : row.isContractRow ? (
                          <>
                            <td className="task-detail__deadlines-level-col task-detail__deadlines-level-col--1 task-detail__td-text">
                              <button
                                type="button"
                                className="contract-detail__deadlines-expand-btn"
                                onClick={toggleDeadlinesTasks}
                                aria-expanded={deadlinesTasksExpanded}
                                aria-label={deadlinesTasksExpanded ? 'Скрыть этапы' : 'Показать этапы'}
                              >
                                <span className="task-detail__chevron" aria-hidden>
                                  {deadlinesTasksExpanded ? '▼' : '▶'}
                                </span>
                              </button>{' '}
                              <strong>{row.contractNumber}</strong>
                            </td>
                            <td className="task-detail__deadlines-level-col task-detail__deadlines-level-col--2" />
                            <td className="task-detail__deadlines-level-col task-detail__deadlines-level-col--3" />
                          </>
                        ) : (
                          <>
                            <td className="task-detail__deadlines-level-col task-detail__deadlines-level-col--1" />
                            {row.showTaskId ? (
                              <td
                                rowSpan={row.taskRowSpan}
                                className="task-detail__deadlines-level-col task-detail__deadlines-level-col--2 task-detail__td-text"
                              >
                                {row.taskId}
                              </td>
                            ) : null}
                            {row.showWorkType ? (
                              <td
                                rowSpan={row.workTypeRowSpan}
                                className="task-detail__deadlines-level-col task-detail__deadlines-level-col--3 task-detail__td-text"
                              >
                                {row.workType}
                              </td>
                            ) : null}
                          </>
                        )}
                        <td className="task-detail__deadlines-date">
                          {cardFieldsEditable && row.isContractRow && row.contractId != null
                            && !(row.contractId === contract?.id && stageDrafts.length > 0) ? (
                            <input
                              type="date"
                              className="contractor-detail__field task-detail__cell-input task-detail__deadlines-date-input"
                              value={deadlineDrafts[row.contractId]?.start_date ?? ''}
                              onChange={(e) => updateDeadlineDraft(row.contractId!, 'start_date', e.target.value)}
                              disabled={cardSaving}
                              aria-label={`Начало по договору ${row.contractNumber}`}
                            />
                          ) : (
                            formatDateShort(
                              row.contractId === contract?.id && cardEditing && stageDrafts.length > 0
                                ? contractDatesFromStages(stageDrafts).start_date
                                : row.startDate,
                            )
                          )}
                        </td>
                        <td className="task-detail__deadlines-date">
                          {cardFieldsEditable && row.isContractRow && row.contractId != null
                            && !(row.contractId === contract?.id && stageDrafts.length > 0) ? (
                            <input
                              type="date"
                              className="contractor-detail__field task-detail__cell-input task-detail__deadlines-date-input"
                              value={deadlineDrafts[row.contractId]?.end_date ?? ''}
                              onChange={(e) => updateDeadlineDraft(row.contractId!, 'end_date', e.target.value)}
                              disabled={cardSaving}
                              aria-label={`Крайний срок по договору ${row.contractNumber}`}
                            />
                          ) : (
                            formatDateShort(
                              row.contractId === contract?.id && cardEditing && stageDrafts.length > 0
                                ? contractDatesFromStages(stageDrafts).end_date
                                : row.endDate,
                            )
                          )}
                        </td>
                        <td className="task-detail__deadlines-date task-detail__deadlines-closing-cell">
                          {row.isTotal ? <strong>{row.expenseClosingDisplay}</strong> : row.expenseClosingDisplay}
                        </td>
                        <td className={deadlineDeltaCellClass()}>
                          {row.isContractRow || row.isTotal ? (
                            <span className={deadlineStatusBadgeClass(row.deltaVariant)}>
                              {row.deltaDisplay}
                            </span>
                          ) : row.isTaskRow ? (
                            row.deltaDisplay
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="task-detail__deadlines-chart-wrap">
                  <TaskDeadlinesGantt
                    className="task-deadlines-gantt--contract-deadlines"
                    rows={visibleDeadlinesData.ganttRows}
                    spanStartMs={visibleDeadlinesData.spanStartMs}
                    spanEndMs={visibleDeadlinesData.spanEndMs}
                    axisStartLabel={visibleDeadlinesData.axisStartLabel}
                    axisEndLabel={visibleDeadlinesData.axisEndLabel}
                    totalOverdue={visibleDeadlinesData.totalOverdue}
                    todayMs={visibleDeadlinesData.todayMs}
                    referenceMarkerMs={visibleDeadlinesData.referenceMarkerMs}
                    referenceMarkerLabel={visibleDeadlinesData.referenceMarkerLabel}
                    showGlobalReferenceMarker={visibleDeadlinesData.summary.allExpensesClosed}
                    showTodayMarker={!visibleDeadlinesData.summary.allExpensesClosed}
                    showLegend
                    alignGroupedHeader
                    showTimelineHeader
                    closingLegendLabel="закрытие этапа бухгалтерией"
                  />
                </div>
              </div>
              </>
            )}
          </div>
        )}

        {activeCardTab === 'workSchedule' && (
          <div className="contractor-detail__tab-panel" role="tabpanel">
            <div className="task-detail__deadlines-layout contract-stages__layout">
              <ContractStagesEditor
                drafts={stageDrafts}
                onChange={handleStageDraftsChange}
                canEdit={cardFieldsEditable}
                disabled={cardSaving}
                taskOptions={canTasks ? stageTaskOptions : []}
                boundTaskIdsByStageId={boundTaskIdsByStageId}
                onBoundTasksChange={handleStageBoundTasksChange}
                canEditTasks={cardFieldsEditable && canTasksEdit}
                compactMetaColumns
                contractOptions={stageContractOptions}
                defaultContractId={mainContract?.id ?? contract?.id ?? null}
              />
              {!cardEditing && workScheduleGantt && workScheduleGantt.rows.length > 0 ? (
                <div className="task-detail__deadlines-chart-wrap">
                  <TaskDeadlinesGantt
                    className="task-deadlines-gantt--contract-deadlines"
                    rows={workScheduleGantt.rows}
                    spanStartMs={workScheduleGantt.spanStartMs}
                    spanEndMs={workScheduleGantt.spanEndMs}
                    axisStartLabel={workScheduleGantt.axisStartLabel}
                    axisEndLabel={workScheduleGantt.axisEndLabel}
                    totalOverdue={workScheduleGantt.totalOverdue}
                    todayMs={workScheduleGantt.todayMs}
                    showTimelineHeader
                    showStatusDesignations={false}
                  />
                </div>
              ) : null}
            </div>
          </div>
        )}

        {activeCardTab === 'budget' && (
          <div className="contractor-detail__tab-panel" role="tabpanel">
            {marginColumnTasks.length === 0 ? (
              <p className="contractor-detail__muted">Нет задач семьи договоров для расчёта маржи.</p>
            ) : (
              <>
                {amountMismatchInfo.mismatch ? (
                  <div className="contract-detail__deadlines-banner contract-detail__deadlines-banner--overdue contract-detail__margin-mismatch-banner">
                    <div className="contract-detail__deadlines-banner-main">
                      <div className="contract-detail__deadlines-banner-heading contract-detail__deadlines-banner-heading--overdue">
                        Сумма по Договору не соответствует сумме по Задачам на{' '}
                        {formatContractSumDisplay(Math.abs(amountMismatchInfo.delta))} руб.
                      </div>
                    </div>
                  </div>
                ) : null}
                <MarginCalcTable
                  columnTasks={marginColumnTasks}
                  headerCells={marginHeaderCells}
                  tableData={marginTableData}
                />
              </>
            )}
          </div>
        )}
      </section>

      {showOperationsBlock ? (
        <TaskOperationsTable
          id="contract-detail-operations"
          operations={displayedOperations}
          filterHint={opsFilterHint}
          onClearFilter={activeCardTab === 'balance' && opsViewFilter.kind === 'balanceCell' ? clearOpsFilter : undefined}
          extraColumns={{
            cash: canOutcomeCash,
            tax: canOutcomeTax,
            servFee: canOutcomeServ,
          }}
        />
      ) : null}

      {paymentNotesOpen && canIncomePlanningView ? (
        <div
          className="contract-detail__notes-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="contract-detail-notes-drawer-title"
        >
          <button
            type="button"
            className="contract-detail__notes-drawer-backdrop"
            aria-label="Закрыть комментарии"
            onClick={closePaymentNotes}
          />
          <aside className="contract-detail__notes-drawer-panel">
            <div className="contract-detail__notes-drawer-head">
              <h2 id="contract-detail-notes-drawer-title" className="contract-detail__notes-drawer-title">
                Комментарии по оплатам
              </h2>
              <button
                type="button"
                className="contract-detail__notes-drawer-close"
                aria-label="Закрыть"
                onClick={closePaymentNotes}
              >
                ×
              </button>
            </div>
            <ContractPaymentNotesChat
              variant="drawer"
              contractId={contract.id}
              notes={familyPaymentNotes}
              canEdit={canIncomePlanningEdit && !cardEditing}
              onNoteAdded={handlePaymentNoteAdded}
            />
          </aside>
        </div>
      ) : null}
    </div>
  );
};

export default ContractDetailPage;
